import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import PageHeader from "@/components/common/PageHeader";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Calendar, Globe, Save, Loader2, User, Briefcase, Flag, Building2, Map } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const fields = [
  { key: "fullName", label: "Full Name", placeholder: "Alex Morgan", span: 2, icon: User },
  { key: "designation", label: "Designation", placeholder: "Senior Product Engineer", span: 2, icon: Briefcase },
  { key: "email", label: "Email", placeholder: "you@domain.com", span: 1, type: "email", icon: Mail },
  { key: "phone", label: "Phone", placeholder: "+1 (415) 555-0100", span: 1, icon: Phone },
  { key: "dob", label: "Date of Birth", placeholder: "", span: 1, type: "date", icon: Calendar },
  { key: "nationality", label: "Nationality", placeholder: "American", span: 1, icon: Flag },
  { key: "address", label: "Address", placeholder: "Street, suite, etc.", span: 2, textarea: true, icon: MapPin },
  { key: "city", label: "City", placeholder: "San Francisco", span: 1, icon: Building2 },
  { key: "state", label: "State", placeholder: "California", span: 1, icon: Map },
  { key: "country", label: "Country", placeholder: "United States", span: 2, icon: Globe }
];

export default function PersonalInformation() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    fullName: "",
    designation: "",
    email: "",
    phone: "",
    dob: "",
    nationality: "",
    address: "",
    city: "",
    state: "",
    country: "",
    linkedinUrl: "",
    githubUrl: "",
    twitterUrl: "",
    websiteUrl: ""
  });
  const [recordId, setRecordId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/PersonalInfo');
        if (res.success && res.data && res.data.length > 0) {
          const data = res.data[0];
          setRecordId(data.id);
          
          let formattedDob = "";
          if (data.dateOfBirth) {
            formattedDob = data.dateOfBirth.split('T')[0];
          }

          setForm({
            fullName: data.fullName || "",
            designation: data.designation || "",
            email: data.email || "",
            phone: data.phone || "",
            dob: formattedDob,
            nationality: data.nationality || "",
            address: data.address || "",
            city: data.city || "",
            state: data.state || "",
            country: data.country || "",
            linkedinUrl: data.linkedinUrl || "",
            githubUrl: data.githubUrl || "",
            twitterUrl: data.twitterUrl || "",
            websiteUrl: data.websiteUrl || ""
          });
        }
      } catch (err) {
        toast.error("Failed to load Personal Info");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      let isoDob = null;
      if (form.dob) {
        isoDob = form.dob;
      }
      
      const { dob, ...rest } = form;
      const payload = {
        ...rest,
        dateOfBirth: isoDob
      };

      if (recordId) {
        const res = await api.put(`/PersonalInfo/${recordId}`, payload);
        if (res.success) toast.success("Personal information updated.");
      } else {
        const res = await api.post('/PersonalInfo', payload);
        if (res.success) {
          setRecordId(res.data);
          toast.success("Personal information created.");
        }
      }
    } catch (err) {
      toast.error(err.message || "Failed to save data");
    }
  };

  if (loading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Personal Information"
        description="Contact and identity details displayed on your portfolio and CV."
        testId="personal"
      />

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <form onSubmit={handleSave} className="xl:col-span-3" data-testid="personal-form">
          <Card className="p-6">
            <h3 className="font-display text-sm font-semibold mb-5">Identity & contact</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {fields.map((f) => (
                <div key={f.key} className={f.span === 2 ? "sm:col-span-2" : ""}>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-1.5">
                    {f.icon && <f.icon className="h-3.5 w-3.5" strokeWidth={2} />}
                    {f.label}
                  </Label>
                  {f.textarea ? (
                    <Textarea
                      data-testid={`personal-${f.key}`}
                      value={form[f.key] || ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      rows={2}
                      className="mt-1.5"
                    />
                  ) : (
                    <Input
                      data-testid={`personal-${f.key}`}
                      type={f.type || "text"}
                      value={form[f.key] || ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      className="mt-1.5"
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end gap-2 mt-6 pt-5 border-t border-border">
              <Button variant="outline" type="button">Cancel</Button>
              <Button type="submit" data-testid="personal-save">
                <Save className="mr-1.5 h-4 w-4" strokeWidth={1.75} />
                Save changes
              </Button>
            </div>
          </Card>
        </form>

        <div className="xl:col-span-2">
          <div className="xl:sticky xl:top-24 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Profile preview
            </p>
            <Card className="p-6" data-testid="personal-preview">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16 rounded-xl">
                  <AvatarFallback className="rounded-lg">{user?.username?.substring(0, 2).toUpperCase() || "AM"}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <h3 className="font-display text-lg font-bold">{form.fullName}</h3>
                  <p className="text-xs font-mono text-muted-foreground truncate">
                    {form.designation}
                  </p>
                  <Badge variant="secondary" className="mt-1.5 text-[10px]">Verified</Badge>
                </div>
              </div>
              <div className="mt-6 space-y-3 text-sm">
                <Row icon={Mail} label="Email" value={form.email} />
                <Row icon={Phone} label="Phone" value={form.phone} />
                <Row icon={Calendar} label="Date of Birth" value={form.dob} />
                <Row icon={Globe} label="Nationality" value={form.nationality} />
                <Row
                  icon={MapPin}
                  label="Location"
                  value={[form.city, form.state, form.country].filter(Boolean).join(", ")}
                />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <span className="h-7 w-7 rounded-md bg-muted flex items-center justify-center shrink-0 mt-0.5">
      <Icon className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
    </span>
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="text-sm font-medium truncate">{value || "—"}</p>
    </div>
  </div>
);
